/**
 * Signal editors (workflow-signals spec 2026-07-11 §3.A). All inputs write
 * through the workflowConfigStore's pruning signal setters — the overlay stays
 * sparse. Mirrors the handoff editors: disabling a base signal is the semantic
 * {enabled:false} patch (never a keyed-section tombstone); added signals are
 * removed by un-adding the overlay entry. NOT Advanced-gated — disabling and
 * retuning signal instructions are primary operator actions on this tab.
 */
import { useState } from 'react'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { InlineFindings } from './entryControls'
import { BilingualTextEditor } from './editors'
import { deriveKey, KEY_RE } from './deriveKey'
import { extractTokens } from '@/utils/friendlyLabels'
import type { ConfigSignalVM } from '@/utils/workflowConfigView'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'

const inputCls =
  'rounded-sm border border-border bg-background p-1 text-xs text-foreground'
const btnCls =
  'rounded-sm border border-border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted'

/** Lifecycle events a signal can fire on (backend SignalSpec `on` enum). */
const SIGNAL_EVENTS = [
  { value: 'payment_received', label: 'Payment received' },
  { value: 'completion', label: 'Order complete' },
] as const

export function SignalControls({ signal }: { signal: ConfigSignalVM }) {
  const setSignalEnabled = useWorkflowConfigStore((s) => s.setSignalEnabled)
  const removeAddedSignal = useWorkflowConfigStore((s) => s.removeAddedSignal)
  if (!signal.id) return null
  if (signal.origin === 'added') {
    return (
      <button type="button" className={btnCls} aria-label={`remove ${signal.id}`}
        onClick={() => removeAddedSignal(signal.id!)}>
        Remove
      </button>
    )
  }
  return signal.disabled ? (
    <button type="button" className={btnCls} aria-label={`re-enable ${signal.id}`}
      onClick={() => setSignalEnabled(signal.id!, true)}>
      Re-enable
    </button>
  ) : (
    <button type="button" className={btnCls} aria-label={`disable ${signal.id}`}
      onClick={() => setSignalEnabled(signal.id!, false)}>
      Disable
    </button>
  )
}

export function SignalInstructionEditor({ signal, base }: {
  signal: ConfigSignalVM
  base: OrderWorkflowDefinitionJson
}) {
  const store = useWorkflowConfigStore()
  if (!signal.id) return null
  const id = signal.id
  const baseInstruction = base.signals?.find((s) => s.id === id)?.instruction ?? {}
  const overlayInstruction =
    (store.overlay.signals?.[id] as { instruction?: Record<string, string> } | undefined)
      ?.instruction ?? {}
  const effective = { ...baseInstruction, ...overlayInstruction }
  return (
    <div className="space-y-1">
      <BilingualTextEditor
        value={effective}
        baseValue={baseInstruction}
        onChange={(lang, text) => store.setSignalInstruction(id, lang, text)}
        tokenLegend={extractTokens(baseInstruction)}
      />
      <InlineFindings paths={[`signals.${id}`]} />
    </div>
  )
}

/**
 * Guided constructor for one NEW signal: overlay `signals.<id>` carrying its
 * lifecycle `on` event + a bilingual instruction. Client-side checks are
 * convenience only; the server's validate pass is the gate. Mirrors
 * AddHandoffRuleForm's derive-key + collapsed-affordance idiom.
 */
export function AddSignalForm({ base }: { base: OrderWorkflowDefinitionJson }) {
  const overlaySignals = useWorkflowConfigStore((s) => s.overlay.signals)
  const addSignal = useWorkflowConfigStore((s) => s.addSignal)

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [id, setId] = useState('')
  const [idTouched, setIdTouched] = useState(false)
  const [event, setEvent] = useState<string>('payment_received')
  const [texts, setTexts] = useState<Record<string, string>>({})

  const languages = base.languages ?? []
  const effectiveId = idTouched ? id : deriveKey(name)
  const existingIds = new Set([
    ...(base.signals ?? []).map((s) => s.id).filter((x): x is string => typeof x === 'string'),
    ...Object.keys(overlaySignals ?? {}),
  ])
  const idTaken = existingIds.has(effectiveId)
  const idInvalid = effectiveId !== '' && !KEY_RE.test(effectiveId)
  const instructionReady = languages.some((lang) => (texts[lang] ?? '').trim() !== '')
  const canSubmit = effectiveId !== '' && !idTaken && !idInvalid && event !== '' && instructionReady

  const reset = () => {
    setOpen(false); setName(''); setId(''); setIdTouched(false)
    setEvent('payment_received'); setTexts({})
  }

  const submit = () => {
    // Build the spec by OMISSION — never write undefined into the overlay.
    const instruction: Record<string, string> = {}
    for (const lang of languages) {
      const text = (texts[lang] ?? '').trim()
      if (text) instruction[lang] = text
    }
    addSignal(effectiveId, { on: event, instruction })
    reset()
  }

  if (!open) {
    return (
      <button
        type="button"
        className="rounded-xl border border-dashed border-border text-muted-foreground px-3 py-1.5 text-xs font-semibold transition hover:border-primary hover:bg-primary/10 hover:text-primary"
        onClick={() => setOpen(true)}
      >
        + Add a signal
      </button>
    )
  }

  return (
    <div className="rounded-xl border border-border p-3 space-y-2 text-xs text-foreground">
      <p className="font-medium text-foreground">Add a signal</p>
      <p className="text-muted-foreground">
        A private instruction the assistant weaves into its reply when this moment fires —
        customers never see it verbatim.
      </p>
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-1.5">
          Name
          <input aria-label="name" className={`${inputCls} w-44`} value={name}
            onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex items-center gap-1.5">
          Id
          <input aria-label="id" className={`${inputCls} w-36 font-mono`} value={effectiveId}
            onChange={(e) => { setId(e.target.value); setIdTouched(true) }} />
        </label>
        <label className="flex items-center gap-1.5">
          Fires on
          <select aria-label="fires on" className={inputCls} value={event}
            onChange={(e) => setEvent(e.target.value)}>
            {SIGNAL_EVENTS.map((ev) => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
          </select>
        </label>
      </div>
      {idTaken && <p className="text-destructive">That signal id already exists in this workflow.</p>}
      {idInvalid && <p className="text-destructive">Ids are lowercase letters, digits and underscores, starting with a letter.</p>}

      <div className="space-y-1">
        {languages.map((lang) => (
          <label key={lang} className="flex items-start gap-1.5">
            <span className="mt-1 font-mono uppercase text-muted-foreground">{lang}</span>
            <textarea
              aria-label={`instruction in ${lang}`}
              className={`${inputCls} w-full`}
              rows={2}
              value={texts[lang] ?? ''}
              onChange={(e) => setTexts((t) => ({ ...t, [lang]: e.target.value }))}
            />
          </label>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <button type="button"
          className="rounded-sm px-3 py-1.5 font-medium text-foreground hover:bg-muted"
          onClick={reset}>
          Cancel
        </button>
        <button type="button" disabled={!canSubmit}
          className="rounded-sm bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          onClick={submit}>
          Add
        </button>
      </div>
    </div>
  )
}
